<template>
  <div class="inspect-arch">
    <h3 class="inspect-arch__title">{{ architecture.title }}</h3>
    <div class="inspect-arch__body">
      <div
        v-for="layer in architecture.layers"
        :key="layer.name"
        class="inspect-arch__row"
      >
        <div class="inspect-arch__label">{{ layer.name }}</div>
        <div class="inspect-arch__panel">
          <div v-if="layer.groups" class="inspect-arch__platform">
            <div
              v-for="(group, groupIndex) in layer.groups"
              :key="groupIndex"
              :class="[
                'inspect-arch__platform-group',
                { 'inspect-arch__platform-group--ai': group.subgroups }
              ]"
            >
              <div v-if="group.name" class="inspect-arch__platform-title">{{ group.name }}</div>
              <div v-if="group.subgroups" class="inspect-arch__subgroups">
                <div
                  v-for="sub in group.subgroups"
                  :key="sub.title"
                  class="inspect-arch__subgroup"
                >
                  <h5>{{ sub.title }}</h5>
                  <div class="inspect-arch__cells inspect-arch__cells--grid">
                    <span
                      v-for="item in sub.items"
                      :key="item"
                      class="inspect-arch__cell"
                    >{{ item }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="inspect-arch__cells inspect-arch__cells--stack">
                <span
                  v-for="item in group.items"
                  :key="item"
                  class="inspect-arch__cell"
                >{{ item }}</span>
              </div>
            </div>
          </div>
          <div v-else-if="layer.sections" class="inspect-arch__infra">
            <div
              v-for="section in layer.sections"
              :key="section.title"
              class="inspect-arch__infra-block"
            >
              <h5>{{ section.title }}</h5>
              <div
                :class="[
                  'inspect-arch__cells',
                  section.type === 'devices' ? 'inspect-arch__cells--devices' : 'inspect-arch__cells--row'
                ]"
              >
                <span
                  v-for="item in section.items"
                  :key="item"
                  class="inspect-arch__cell"
                >{{ section.type === 'devices' ? deviceIcon(item) : item }}</span>
              </div>
            </div>
          </div>
          <div v-else class="inspect-arch__cells inspect-arch__cells--row">
            <span
              v-for="item in layer.items"
              :key="item"
              class="inspect-arch__cell"
            >{{ item }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InspectionArchDiagram',
  props: {
    architecture: {
      type: Object,
      required: true
    }
  },
  methods: {
    deviceIcon(name) {
      const icons = {
        球机: '📹',
        枪机: '🎥',
        半球: '📷',
        高空瞭望: '🔭'
      }
      return icons[name] ? `${icons[name]} ${name}` : name
    }
  }
}
</script>

<style scoped>
.inspect-arch {
  max-width: 1200px;
  margin: 0 auto;
  border: 1px solid #b3d4f0;
  border-radius: 4px;
  overflow: hidden;
  background: linear-gradient(180deg, #f5faff 0%, #eef6fc 100%);
}

.inspect-arch__title {
  margin: 0;
  padding: 18px 24px;
  font-size: 22px;
  font-weight: 700;
  color: #1565c0;
  text-align: center;
  border-bottom: 1px solid #b3d4f0;
  background: #fff;
}

.inspect-arch__body {
  display: flex;
  flex-direction: column;
}

.inspect-arch__row {
  display: flex;
  border-bottom: 1px solid #b3d4f0;
}

.inspect-arch__row:last-child {
  border-bottom: none;
}

.inspect-arch__label {
  flex: 0 0 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  background: linear-gradient(180deg, #1976d2, #1565c0);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 4px;
  padding: 16px 0;
}

.inspect-arch__panel {
  flex: 1;
  padding: 14px 16px;
  min-width: 0;
}

.inspect-arch__cells {
  display: flex;
  gap: 10px;
}

.inspect-arch__cells--row {
  flex-wrap: wrap;
}

.inspect-arch__cells--row .inspect-arch__cell {
  flex: 1 1 0;
  min-width: 110px;
}

.inspect-arch__cells--grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.inspect-arch__cells--stack {
  flex-direction: column;
}

.inspect-arch__cells--devices .inspect-arch__cell {
  flex: 1;
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}

.inspect-arch__cell {
  background: linear-gradient(180deg, #42a5f5, #1976d2);
  border: 1px solid #1565c0;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  padding: 10px 6px;
  line-height: 1.4;
  border-radius: 3px;
}

.inspect-arch__platform {
  display: grid;
  grid-template-columns: 2.5fr 1fr;
  gap: 0;
  align-items: stretch;
}

.inspect-arch__platform-group {
  border: 1px dashed #5c9fd4;
  border-radius: 4px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.75);
  min-height: 100%;
}

.inspect-arch__platform-group--ai {
  margin-right: 28px;
  position: relative;
}

.inspect-arch__platform-group--ai::after {
  content: '→';
  position: absolute;
  right: -22px;
  top: 50%;
  transform: translateY(-50%);
  color: #1976d2;
  font-size: 20px;
  font-weight: 700;
}

.inspect-arch__platform-title {
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #1565c0;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #bbdefb;
}

.inspect-arch__subgroups {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 12px;
}

.inspect-arch__subgroup h5 {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 700;
  color: #1976d2;
  text-align: center;
}

.inspect-arch__infra {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
}

.inspect-arch__infra-block h5 {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 700;
  color: #1565c0;
  text-align: center;
}

@media (max-width: 960px) {
  .inspect-arch__label {
    flex: 0 0 40px;
    font-size: 13px;
    letter-spacing: 2px;
  }

  .inspect-arch__cells--grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .inspect-arch__platform {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .inspect-arch__platform-group--ai {
    margin-right: 0;
    margin-bottom: 24px;
  }

  .inspect-arch__platform-group--ai::after {
    right: auto;
    left: 50%;
    top: auto;
    bottom: -20px;
    transform: translateX(-50%) rotate(90deg);
  }

  .inspect-arch__subgroups {
    grid-template-columns: 1fr;
  }

  .inspect-arch__infra {
    grid-template-columns: 1fr;
  }

  .inspect-arch__cells--row .inspect-arch__cell {
    min-width: calc(50% - 10px);
    flex: 1 1 calc(50% - 10px);
  }
}

@media (max-width: 560px) {
  .inspect-arch__cells--grid {
    grid-template-columns: 1fr;
  }

  .inspect-arch__cells--row .inspect-arch__cell {
    min-width: 100%;
    flex: 1 1 100%;
  }
}
</style>
